/**
 * Injectable translator for the rule engine's validation messages.
 *
 * Replaces a direct import of `~/localization/i18n`, a side-effecting
 * module-load singleton: importing it calls
 * `i18n.use(Backend).use(LanguageDetector).use(initReactI18next).init(...)`,
 * kicking off an HTTP fetch for translation files, and it reads
 * `__I18N_VERSION__`, a Vite `define` global that exists only in the web app's
 * build. Neither is available to a shared package.
 *
 * The app injects i18next's `t` at its composition root. The default echoes the
 * key back, which is what the engine's own tests want — they assert on stable
 * keys like `validation.required`, not localized prose.
 *
 * ⚠️ That default is also why an unwired seam is dangerous: nothing throws, the
 * messages simply degrade from localized text to raw keys in the UI, and no
 * engine test would notice because the tests want the key.
 * `formEngineWiring.test.js` is what catches it. (The Viedoc.Me.ReactNative POC
 * shipped exactly this bug.)
 */

/** @typedef {(key: string, params?: Record<string, unknown>) => string} TranslateFn */

/** @type {TranslateFn} */
const defaultTranslator = (key) => key;

let currentTranslator = defaultTranslator;

/** Inject the real translator. Call once, at the app's composition root. */
export function setTranslator(translate) {
    currentTranslator = translate;
}

export function getTranslator() {
    return currentTranslator;
}

/**
 * Restore the key-echoing default.
 *
 * Module-level mutable state: a test that injects a translator leaks into every
 * other test file sharing the worker unless it resets. Call from `afterEach`
 * wherever `setTranslator` is used.
 */
export function resetTranslator() {
    currentTranslator = defaultTranslator;
}
